"use client";

import Image from "next/image";
import type { ReactNode } from "react";

import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import type { ToolEntry } from "./data";
import type { ToolPreviewVariant } from "./previews/types";
import { ToolPreviewShell } from "./tool-preview-shell";

const GRID_PATTERN = "/assets-next/tools/card-grid-pattern.svg";

interface ToolCardProps {
  tool: ToolEntry;
  /** Static HTML preview rendered inside the white bleed panel. */
  preview?: ReactNode;
  previewVariant?: ToolPreviewVariant;
  className?: string;
}

/**
 * Toolkit tab card — grey tile with dot-grid texture, live title/subtitle, and a
 * static HTML preview illustration bleeding off the bottom-right (Figma 11043:69729).
 *
 * `@container` drives preview shell size/position between xs (~283px) and xl (~566px)
 * card widths — not the viewport breakpoint.
 */
export function ToolCard({
  tool,
  preview,
  previewVariant = "compact",
  className,
}: ToolCardProps) {
  const isLarge = previewVariant === "large";

  const body = (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl"
      >
        <Image
          src={GRID_PATTERN}
          alt=""
          fill
          className="object-none object-left-top opacity-100"
          sizes="735px"
        />
      </div>

      <div
        className={cn(
          "relative z-10 flex max-w-full flex-col gap-2 p-5 @lg:p-7",
          isLarge ? "max-w-[228px] @lg:max-w-[228px]" : "max-w-[228px]"
        )}
      >
        <h3 className="text-ds-body-md @lg:text-ds-body-lg text-grey-950 tracking-[-0.02em]">
          {tool.title}
        </h3>
        {tool.subtitle && (
          <p className="text-ds-body-xs @lg:text-ds-body-sm text-grey-500 tracking-[-0.02em]">
            {tool.subtitle}
          </p>
        )}
      </div>

      {preview && (
        <ToolPreviewShell variant={previewVariant}>{preview}</ToolPreviewShell>
      )}
    </>
  );

  const tileClass = cn(
    "border-grey-100 bg-grey-50 @container relative h-[240px] overflow-hidden rounded-2xl border",
    className
  );

  if (!tool.href) {
    return <div className={tileClass}>{body}</div>;
  }

  return (
    <Link
      href={tool.href}
      className={cn(
        tileClass,
        "hover:border-grey-200 transition-colors focus-visible:ring-2 focus-visible:ring-[#001eff] focus-visible:ring-offset-2 focus-visible:outline-none"
      )}
    >
      {body}
    </Link>
  );
}
